import { api } from "../services/api";
import Router from "next/router";
import { createReduxModule } from "hooks-for-redux";
import { siteDataStore } from "./siteData";
import { gaEvent } from "../lib/ga";
import { gamePlayStore } from "./gamePlay";
import { userStore } from "./user";
import { useLongPolling } from "../services/websocket";

export const ERROR_GAME_SYNC = 0; // used on server
export const ERROR_END_GAME_WRONG_ID = 1;
export const ERROR_TIME_UP_NOT_MY_TURN = 2;
export const ERROR_LEFT_GAME_STILL_THERE = 3;
export const ERROR_ELO_DONT_MATCH = 4;
export const ERROR_WON_LOWER_ELO = 5;
export const ERROR_TIME_FEN = 6;
export const ERROR_MOVE_RECIVED = 7;
export const ERROR_BOUNDRY = 8;
export const ERROR_CLOCK_STOPPED = 9;
export const ERROR_PLAYER = 10;

const initialState = {
  last: null,
  max: null,
  min: null,
  sum: 0,
  count: 0,
  average: 0,
  error: 0,
  offline: 0,
  resends: 0,
  pings: [],
  pingStart: null,
  wsClose: 0,
  userWsClose: 0,
  wsCloseErrorMsg: "",
  wsError: 0,
  wsErrorMsg: "",
  retries: 0,
  clientDisconn: 0,
  disconnectTime: null,
  disconnectTimeStamp: null,
  timeSinceDisconnect: null,
  endTimestamp: null,
  reconnected: 0,
  reconnectFail: null,
  updateReceived: null,
  updateReceivedTimeStamp: null,
  gamePlayState: null,
  errorData: {
    v: "8",
    deviceInfo: {
      isMobile:
        process.browser &&
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      platform: process.browser ? navigator.platform : "unknown",
    },
    connectionState: {
      visibilityState: process.browser ? document.visibilityState : "unknown",
      wasHidden: false,
      wasVisible: false,
      lastVisibilityChange: null,
      lastPageHide: null,
      lastPageShow: null,
      lastBeforeUnload: null,
      lastUnload: null,
      lastFocus: null,
      lastBlur: null,
    },
    networkState: process.browser
      ? navigator.onLine
        ? "online"
        : "offline"
      : "unknown",
    networkStateTime: null,
    networkStateOnClose: null,
    networkStateTimeOnClose: null,
    wsCloseGameEnded: null,
    wsCloseIsPlaying: null,
    wsCloseTime: null,
    wsCloseGameId: null,
    longPollingSwitchAttempted: false,
    longPollingSwitchStartTime: null,
    longPollingSwitchEndTime: null,
    longPollingSwitchCompleted: false,
    longPollingSwitchFailed: false,
    longPollingSwitchError: null,
    longPollingSwitchGameId: null,
  },
};

export const [
  usePings,
  {
    moveSent,
    moveConfirmed,
    reset,
    setErrorData,
    setError,
    setOffline,
    setWsClose,
    setWsError,
    setWsCloseErrorMsg,
    setWsErrorMsg,
    setRetries,
    resetRetries,
    setClientDisconn,
    setDisconnectTime,
    setReconnected,
    setReconnectFail,
    setUpdateReceived,
    setGamePlayState,
  },
  pingStore,
] = createReduxModule("ping", initialState, {
  moveSent: (state) => ({
    ...state,
    pingStart: Date.now(),
  }),
  moveConfirmed: (state) => {
    const last = Date.now() - state.pingStart;
    const max = state.max != null ? Math.max(state.max, last) : last;
    const min = state.min != null ? Math.min(state.min, last) : last;
    const sum = last + state.sum;
    const count = state.count + 1;
    const average = sum / count;
    const pings = [...state.pings, last];

    return {
      ...state,
      last,
      sum,
      max,
      min,
      count,
      average,
      pings,
    };
  },
  reset: () => initialState,
  setErrorData: (state, data) => ({
    ...state,
    errorData: { ...state.errorData, ...data },
  }),
  setError: (state, errorType) => {
    const endTimestamp = Date.now();
    const timeSinceDisconnect = endTimestamp - state.disconnectTimeStamp;

    return {
      ...state,
      timeSinceDisconnect,
      endTimestamp,
      error: state.error ? state.error * 10 + errorType : errorType,
    };
  },
  setOffline: (state) => ({
    ...state,
    offline: state.offline + 1,
  }),
  setWsClose: (state) => ({
    ...state,
    wsClose: state.wsClose + 1,
  }),
  setWsCloseErrorMsg: (state, msg) => ({
    ...state,
    wsCloseErrorMsg: msg,
  }),
  setWsError: (state) => ({
    ...state,
    wsError: state.wsError + 1,
  }),
  setWsErrorMsg: (state, msg) => ({
    ...state,
    wsErrorMsg: msg,
  }),
  setRetries: (state) => ({
    ...state,
    retries: state.retries + 1,
  }),
  resetRetries: (state) => ({
    ...state,
    retries: 0,
  }),
  setClientDisconn: (state) => ({
    ...state,
    clientDisconn: state.clientDisconn + 1,
  }),
  setReconnected: (state) => ({
    ...state,
    reconnected: state.reconnected + 1,
    disconnectTime: Date.now() - state.disconnectTimeStamp,
  }),
  setDisconnectTime: (state) => ({
    ...state,
    disconnectTimeStamp: Date.now(),
  }),
  setReconnectFail: (state, msg) => ({
    ...state,
    reconnectFail: msg,
  }),
  setUpdateReceived: (state, gameId) => ({
    ...state,
    updateReceived: gameId,
    updateReceivedTimeStamp: Date.now(),
  }),
  setGamePlayState: (state, gamePlayState) => ({
    ...state,
    gamePlayState,
  }),
});

// Initialize connection state tracking
function initializeConnectionTracking() {
  // Only initialize once
  if (typeof window === "undefined") return;

  document.addEventListener("visibilitychange", () => {
    const state = pingStore.getState();
    const now = Date.now();
    const wasHidden = document.visibilityState === "hidden";
    const wasVisible = document.visibilityState === "visible";

    setErrorData({
      connectionState: {
        ...state.errorData.connectionState,
        visibilityState: document.visibilityState,
        wasHidden: wasHidden || state.errorData.connectionState.wasHidden,
        wasVisible: wasVisible || state.errorData.connectionState.wasVisible,
        lastVisibilityChange: now,
      },
    });
  });

  window.addEventListener("pagehide", () => {
    const state = pingStore.getState();
    setErrorData({
      connectionState: {
        ...state.errorData.connectionState,
        lastPageHide: Date.now(),
      },
    });
  });

  window.addEventListener("pageshow", () => {
    const state = pingStore.getState();
    setErrorData({
      connectionState: {
        ...state.errorData.connectionState,
        lastPageShow: Date.now(),
      },
    });
  });

  window.addEventListener("beforeunload", () => {
    const state = pingStore.getState();
    setErrorData({
      connectionState: {
        ...state.errorData.connectionState,
        lastBeforeUnload: Date.now(),
      },
    });
  });

  window.addEventListener("unload", () => {
    const state = pingStore.getState();
    setErrorData({
      connectionState: {
        ...state.errorData.connectionState,
        lastUnload: Date.now(),
      },
    });
  });

  window.addEventListener("focus", () => {
    const state = pingStore.getState();
    setErrorData({
      connectionState: {
        ...state.errorData.connectionState,
        lastFocus: Date.now(),
      },
    });
  });

  window.addEventListener("blur", () => {
    const state = pingStore.getState();
    setErrorData({
      connectionState: {
        ...state.errorData.connectionState,
        lastBlur: Date.now(),
      },
    });
  });
}

// Initialize tracking when module is loaded
process.browser && initializeConnectionTracking();

function analyzeErrorScenario(error: number, ping: any): string {
  const {
    wsClose,
    wsCloseErrorMsg,
    offline,
    disconnectTimeStamp,
    updateReceivedTimeStamp,
    endTimestamp,
    timeSinceDisconnect,
    gamePlayState,
    errorData,
  } = ping;

  const scenarios = [];

  // Check if it's a mobile device
  const isMobile = errorData?.deviceInfo?.isMobile;

  // Check if the game had ended before disconnect
  const gameEndedBeforeDisconnect =
    gamePlayState?.gameEnded &&
    disconnectTimeStamp > gamePlayState.gameEndedTimestamp;

  // Check if there was a visibility change
  const hadVisibilityChange = errorData?.connectionState?.lastVisibilityChange;
  const wasHidden = errorData?.connectionState?.wasHidden;
  // const wasVisible = errorData?.connectionState?.wasVisible;

  // Check if there was a page unload
  const hadPageUnload = errorData?.connectionState?.lastUnload;
  const hadPageHide = errorData?.connectionState?.lastPageHide;

  // Check if there was a focus/blur event
  const hadFocusChange =
    errorData?.connectionState?.lastFocus ||
    errorData?.connectionState?.lastBlur;

  // Analyze WebSocket close code
  const wsCode = wsCloseErrorMsg?.split(" ")[0];
  const isAbnormalClose = wsCode === "1006";
  const isNormalClose = wsCode === "1000";

  // Build scenario description
  if (gameEndedBeforeDisconnect) {
    scenarios.push("Game had already ended before disconnect");
  }

  if (isMobile) {
    if (wasHidden && hadVisibilityChange) {
      // Check if device went to sleep after disconnect
      if (disconnectTimeStamp && hadVisibilityChange > disconnectTimeStamp) {
        scenarios.push("Device went to sleep while disconnected");
      } else {
        scenarios.push("Mobile device went to sleep");
      }
    }
    if (hadPageHide && !hadPageUnload) {
      // Check if app was backgrounded after disconnect
      if (disconnectTimeStamp && hadPageHide > disconnectTimeStamp) {
        scenarios.push("App was backgrounded while disconnected");
      } else {
        scenarios.push("Mobile app was backgrounded");
      }
    }
  }

  if (hadPageUnload) {
    scenarios.push("Browser/app was closed");
  }

  if (hadFocusChange) {
    scenarios.push("User switched to another window/app");
  }

  if (offline > 0) {
    scenarios.push("Device went offline");
  }

  if (isAbnormalClose) {
    scenarios.push("Connection was lost without proper close");
  }

  if (isNormalClose) {
    scenarios.push("Connection was closed normally");
  }

  // Add timing information
  if (timeSinceDisconnect) {
    const disconnectDuration = Math.round(timeSinceDisconnect / 1000);
    scenarios.push(`Disconnected for ${disconnectDuration} seconds`);
  }

  // Add error type context with timing analysis
  switch (error) {
    case ERROR_LEFT_GAME_STILL_THERE:
      scenarios.push("Player was still in game when connection lost");
      break;
    case ERROR_TIME_UP_NOT_MY_TURN: {
      // Calculate time differences
      const disconnectToEnd = endTimestamp - disconnectTimeStamp;
      const updateToEnd = endTimestamp - updateReceivedTimeStamp;

      // Convert to seconds for readability
      const disconnectToEndSeconds = Math.round(disconnectToEnd / 1000);
      const updateToEndSeconds = Math.round(updateToEnd / 1000);

      if (disconnectToEndSeconds <= 5) {
        scenarios.push(
          "Time ran out immediately after disconnect (within 5 seconds)"
        );
      } else if (disconnectToEndSeconds <= 30) {
        scenarios.push(
          "Time ran out shortly after disconnect (within 30 seconds)"
        );
      } else {
        scenarios.push("Time ran out after extended disconnect period");
      }

      if (updateToEndSeconds <= 5) {
        scenarios.push(
          "Game ended immediately after last update (within 5 seconds)"
        );
      } else if (updateToEndSeconds <= 30) {
        scenarios.push(
          "Game ended shortly after last update (within 30 seconds)"
        );
      } else {
        scenarios.push("Game ended after extended period without updates");
      }

      // Add specific timing information
      scenarios.push(`Disconnect to end time: ${disconnectToEndSeconds}s`);
      scenarios.push(`Last update to end time: ${updateToEndSeconds}s`);
      break;
    }
    case ERROR_ELO_DONT_MATCH:
      scenarios.push("Elo mismatch detected");
      break;
    case ERROR_WON_LOWER_ELO:
      scenarios.push("Won against lower elo opponent");
      break;
    case ERROR_MOVE_RECIVED:
      scenarios.push("Move received after game ended");
      break;
    case ERROR_TIME_FEN:
      scenarios.push("Time/FEN mismatch");
      break;
    case ERROR_END_GAME_WRONG_ID:
      scenarios.push("Game ID mismatch at end");
      break;
    case ERROR_CLOCK_STOPPED:
      scenarios.push("Clock stopped unexpectedly");
      break;
  }

  return scenarios.join(" | ");
}

export function sendPingData() {
  const gameId = gamePlayStore.getState().gameId;
  const username = userStore.getState().username;
  const userId = userStore.getState().id;
  const ping = pingStore.getState();

  if (siteDataStore.getState().iframeMode) {
    gaEvent({ category: "game", action: "iframe game ended" });
    return;
  }

  const locale = Router.locale;
  const gameIdInt = gameId ? parseInt(gameId.substring(8)) : 0;

  gaEvent({ category: "game", action: "game ended" });

  if (ping.error) {
    const errorScenario = analyzeErrorScenario(ping.error, ping);

    const errorObj = {
      pings: ping.pings.length,
      ...(ping.wsClose && { wsClose: ping.wsClose }),
      // ...(ping.offline && { offline: ping.offline }),
      ...(ping.wsError && { wsError: ping.wsError }),
      ...(ping.wsCloseErrorMsg && { wsMsg: ping.wsCloseErrorMsg }),
      ...(ping.retries && { retries: ping.retries }),
      ...(ping.clientDisconn && { clientDisconn: ping.clientDisconn }),
      ...(ping.reconnected && { reconnected: ping.reconnected }),
      ...(ping.disconnectTime && { dTime: ping.disconnectTime }),
      ...(ping.disconnectTimeStamp && { dTimeStamp: ping.disconnectTimeStamp }),
      ...(ping.updateReceived && { updateGameId: ping.updateReceived }),
      ...(ping.gamePlayState && { gamePlayState: ping.gamePlayState }),
      ...(ping.timeSinceDisconnect && {
        timeSinceDisconnect: ping.timeSinceDisconnect,
      }),
      ...(ping.endTimestamp && { endTimestamp: ping.endTimestamp }),
      ...(ping.updateReceivedTimeStamp && {
        updateReceivedTimeStamp: ping.updateReceivedTimeStamp,
      }),
      username,
      useLongPolling,
      ...ping.errorData,
      errorScenario,
    };

    const errorData = JSON.stringify(errorObj);
    gaEvent({
      category: "game",
      action: "game ended with error",
      label: ping.error.toString(),
    });

    if (ping.error == ERROR_TIME_UP_NOT_MY_TURN) {
      gaEvent({
        category: "game",
        action: "game ended with error 2 - time up not my turn",
        label: ping.error.toString(),
      });
    }
    if (ping.error == ERROR_LEFT_GAME_STILL_THERE) {
      gaEvent({
        category: "game",
        action: "game ended with error 3 - left game still there",
        label: ping.error.toString(),
      });
    }

    api
      .post("/error", {
        ...ping,
        errorData,
        userId,
        locale,
        gameId: gameIdInt,
      })
      .then(() => {
        reset();
      })
      .catch(() => {
        console.log("post ping failed");
      });
  } else {
    if (ping.count == 0) {
      reset();
      return;
    }

    api
      .post("/ping-stat", {
        min: ping.min,
        max: ping.max,
        average: ping.average,
      })
      .then(() => {
        reset();
      })
      .catch(() => {
        console.log("post ping failed");
      });
  }
}
